import { RefObject, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOnClickOutside } from "usehooks-ts";

import { useBooking } from "@hooks/useBooking/useBooking";
import { timeMapObj } from "@models/services/booking";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";

import TimeSlotButton from "../TimeSlotButton/TimeSlotButton";

interface TimeSlotProps {
  isBookingDisable: boolean;
  isReservationSlotActive: boolean;
  selectedSlotGroup: string;
  setSelectedSlotGroup: (value: string) => void;
}

const TimeSlot = ({
  isBookingDisable,
  isReservationSlotActive,
  selectedSlotGroup,
  setSelectedSlotGroup,
}: TimeSlotProps) => {
  const dispatch = useDispatch();

  // Create a ref container for multiple refs
  const timeSlotRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const { isSingleTimeSlot, selectedSlot, selectedTime, timeMaps } =
    useSelector((State: RootState) => State.booking);

  const { disableTimeMap } = useBooking();

  // Create a wrapper div ref to handle outside clicks
  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(wrapperRef as RefObject<HTMLDivElement>, () => {
    setSelectedSlotGroup("");
  });

  const hasSelectedTime = (obj: Array<timeMapObj>, selectedTime: string) =>
    obj.some(item => item.time === selectedTime);

  const isDisableTimeSlot = (obj: Array<timeMapObj>) =>
    (Object.values(obj).every(item => item.disabled === true) ||
      isBookingDisable) &&
    !isReservationSlotActive
      ? "disabled"
      : "";

  const changeSelectedTime = (time: string, isSlotDisabled: boolean) => {
    if (isSlotDisabled) {
      return;
    }
    dispatch(
      bookingSlice.actions.changeSelectedTime(selectedTime === time ? "" : time)
    );
    setSelectedSlotGroup("");
  };

  return (
    <div
      ref={wrapperRef}
      className={`fg-time-bubbles-wrap ${disableTimeMap(selectedSlot || {})}`}
      data-testid="time-select-test"
    >
      {timeMaps?.map((obj, index) => {
        if (isSingleTimeSlot || obj.length === 1) {
          return obj.map(item => {
            const isSlotDisabled = item.disabled || isBookingDisable;
            return (
              <TimeSlotButton
                disabled={isSlotDisabled}
                key={item.time}
                time={item.time}
                selectedTime={selectedTime}
                onClick={() => changeSelectedTime(item.time, isSlotDisabled)}
              />
            );
          });
        }

        return (
          <div
            ref={el => {
              timeSlotRefs.current[index] = el;
            }}
            className="fg-time-slots-wrap"
            key={obj[0]?.time}
          >
            <div
              className={`fg-time-slots ${selectedSlotGroup === obj[0].time ? "active" : ""}`}
            >
              <button
                className={`btn fg-btn fg-btn-small ${hasSelectedTime(obj, selectedTime) && !isReservationSlotActive ? "active" : ""} ${isDisableTimeSlot(obj)}`}
                onClick={() => {
                  const newValue = obj[0].time;
                  setSelectedSlotGroup(newValue);
                }}
              >
                {hasSelectedTime(obj, selectedTime)
                  ? selectedTime
                  : obj[0].time}
              </button>
              <div className="fg-time-slots-btn-wrap ">
                {obj.map(item => {
                  const isSlotDisabled = item.disabled || isBookingDisable;
                  return (
                    <TimeSlotButton
                      disabled={isSlotDisabled}
                      key={item.time}
                      time={item.time}
                      selectedTime={selectedTime}
                      onClick={() =>
                        changeSelectedTime(item.time, isSlotDisabled)
                      }
                    />
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TimeSlot;
